import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import {
  ThemeProvider as MUIThemeProvider,
  createTheme,
} from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { useTranslation } from "react-i18next";
import { lightTheme, darkTheme } from "../theme";
import { CssBaseline } from "@mui/material";
import { userAPI } from "../services/api";

const ThemeContext = createContext();

// Create RTL and LTR caches
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

const cacheLtr = createCache({
  key: "muiltr",
  stylisPlugins: [prefixer],
});

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Try to get theme from localStorage
    try {
      const savedTheme = localStorage.getItem("theme");
      return savedTheme === "dark";
    } catch {
      return false;
    }
  });

  const [direction, setDirection] = useState("ltr");
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();

  // Load user settings if logged in
  useEffect(() => {
    const loadUserSettings = async () => {
      const token = localStorage.getItem("token");
      // Immediately set loading false if no token
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await userAPI.getSettings();
        if (response.data?.theme) {
          const shouldBeDark = response.data.theme === "dark";
          if (shouldBeDark !== isDarkMode) {
            setIsDarkMode(shouldBeDark);
            localStorage.setItem("theme", shouldBeDark ? "dark" : "light");
          }
        }
      } catch (error) {
        console.error("Failed to load user theme settings:", error);
      } finally {
        setLoading(false);
      }
    };
    loadUserSettings();
  }, []);

  useEffect(() => {
    const dir = i18n.language === "ar" ? "rtl" : "ltr";
    setDirection(dir);
    document.dir = dir;
  }, [i18n.language]);

  const theme = useMemo(
    () =>
      createTheme({
        ...(isDarkMode ? darkTheme : lightTheme),
        direction,
        palette: {
          mode: isDarkMode ? "dark" : "light",
          ...(isDarkMode ? darkTheme.palette : lightTheme.palette),
        },
      }),
    [isDarkMode, direction]
  );

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const newValue = !prev;
      try {
        localStorage.setItem("theme", newValue ? "dark" : "light");
      } catch (error) {
        console.error("Failed to save theme preference:", error);
      }
      return newValue;
    });
  };

  if (loading) {
    return (
      <ThemeContext.Provider value={{ isDarkMode, toggleTheme, direction }}>
        <CacheProvider value={direction === "rtl" ? cacheRtl : cacheLtr}>
          <MUIThemeProvider theme={theme}>
            <CssBaseline />
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
              }}
            >
              Loading theme...
            </div>
          </MUIThemeProvider>
        </CacheProvider>
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, direction }}>
      <CacheProvider value={direction === "rtl" ? cacheRtl : cacheLtr}>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </MUIThemeProvider>
      </CacheProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
